/**
 * Task Management Dashboard - Monitorizare Celery Tasks
 * 
 * Organizat pe categorii:
 * - HIS Integration (eLogin, EasyMedical)
 * - RIS Sync (sincronizare studii)
 * - Detection (segmente)
 * - System (maintenance)
 */

import React, { useState, useEffect, useCallback } from 'react'
import {
  Box,
  Typography,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Button,
  CircularProgress,
  Alert,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material'
import {
  Refresh as RefreshIcon,
  PlayArrow as PlayIcon,
  CheckCircle as SuccessIcon,
  Error as ErrorIcon,
  HourglassEmpty as PendingIcon,
  LocalHospital as HisIcon,
  Sync as SyncIcon,
  Search as DetectionIcon,
  Settings as SystemIcon,
  Schedule as ScheduleIcon,
  Storage as QueueIcon,
} from '@mui/icons-material'
import { toast } from 'react-hot-toast'
import { getAccessToken } from '../../utils/tokenStorage'

// Types
interface ScheduledTask {
  name: string
  category: string
  schedule: string
  description: string
  enabled: boolean
  task_key: string
}

interface TaskHistoryItem {
  id: string
  category: string
  task_type: string
  description: string
  status: string
  timestamp: string
  duration_seconds: number | null
  error_message: string | null
  patient_info: string | null
  study_info: string | null
  pacs_name: string | null
}

interface CategoryStats {
  category: string
  display_name: string
  total: number
  success: number
  failed: number
  icon: string
}

interface QueueInfo {
  name: string
  display_name: string
  length: number
  status: string
}

interface EasyMedicalRequest {
  request_id: number
  patient: string
  status: string
  created_at: string
  has_study: boolean
}

interface DashboardData {
  scheduled_tasks: Record<string, ScheduledTask[]>
  recent_tasks: Record<string, TaskHistoryItem[]>
  category_stats: CategoryStats[]
  queues: QueueInfo[]
  summary: {
    period_hours: number
    total_tasks: number
    success_rate: number
    failed_count: number
    queue_total: number
    easymedical: {
      total_requests: number
      pending: number
      processing: number
      completed: number
      failed: number
      recent_requests: EasyMedicalRequest[]
    }
  }
}

// Helper functions
const formatDuration = (seconds: number | null): string => {
  if (seconds === null) return '-'
  if (seconds < 1) return `${Math.round(seconds * 1000)}ms`
  if (seconds < 60) return `${seconds.toFixed(1)}s`
  return `${Math.floor(seconds / 60)}m ${Math.round(seconds % 60)}s`
}

const formatTime = (dateStr: string): string => {
  const date = new Date(dateStr)
  return date.toLocaleTimeString('ro-RO', { hour: '2-digit', minute: '2-digit' })
}

const formatDateTime = (dateStr: string): string => {
  const date = new Date(dateStr)
  return date.toLocaleString('ro-RO', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  })
}

const getStatusColor = (status: string): 'success' | 'error' | 'warning' | 'info' | 'default' => {
  switch (status.toLowerCase()) {
    case 'success':
    case 'completed':
      return 'success'
    case 'failed':
    case 'error':
      return 'error'
    case 'pending':
    case 'processing':
      return 'warning'
    default:
      return 'default'
  }
}

const getCategoryIcon = (category: string) => {
  switch (category) {
    case 'his':
      return <HisIcon />
    case 'ris':
      return <SyncIcon />
    case 'detection':
      return <DetectionIcon />
    case 'system':
      return <SystemIcon />
    default:
      return <SyncIcon />
  }
}

const getCategoryColor = (category: string): string => {
  switch (category) {
    case 'his':
      return '#2196f3'  // Blue
    case 'ris':
      return '#4caf50'  // Green
    case 'detection':
      return '#ff9800'  // Orange
    case 'system':
      return '#9e9e9e'  // Grey
    default:
      return '#757575'
  }
}

const TaskManagementTab: React.FC = () => {
  const [loading, setLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)
  const [data, setData] = useState<DashboardData | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [autoRefresh, setAutoRefresh] = useState(true)
  const [historyHours, setHistoryHours] = useState(24)
  const [activeCategory, setActiveCategory] = useState('his')
  const [triggeringTask, setTriggeringTask] = useState<string | null>(null)

  const fetchDashboard = useCallback(async (showRefreshing = false) => {
    try {
      if (showRefreshing) setRefreshing(true)
      
      const response = await fetch(`/api/v1/admin/task-management/dashboard?hours=${historyHours}`, {
        headers: { 'Authorization': `Bearer ${getAccessToken()}` }
      })
      
      if (!response.ok) throw new Error('Failed to fetch')
      
      const result = await response.json()
      setData(result)
      setError(null)
    } catch (err) {
      setError('Eroare la încărcarea dashboard-ului')
      console.error('Error:', err)
    } finally {
      setLoading(false)
      setRefreshing(false)
    }
  }, [historyHours])

  useEffect(() => {
    fetchDashboard()
  }, [fetchDashboard])

  useEffect(() => {
    if (!autoRefresh) return
    const interval = setInterval(() => fetchDashboard(true), 30000)
    return () => clearInterval(interval)
  }, [autoRefresh, fetchDashboard])

  const handleTriggerTask = async (taskKey: string) => {
    try {
      setTriggeringTask(taskKey)
      
      const response = await fetch(`/api/v1/admin/task-management/trigger/${taskKey}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${getAccessToken()}` }
      })
      
      if (!response.ok) {
        const err = await response.json()
        throw new Error(err.detail || 'Failed')
      }
      
      toast.success('Task declanșat cu succes!')
      setTimeout(() => fetchDashboard(true), 2000)
    } catch (err: any) {
      toast.error(err.message || 'Eroare')
    } finally {
      setTriggeringTask(null)
    }
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    )
  }

  if (error || !data) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        {error || 'Nu s-au putut încărca datele'}
        <Button onClick={() => fetchDashboard()} sx={{ ml: 2 }}>Reîncearcă</Button>
      </Alert>
    )
  }

  const categories = [
    { key: 'his', label: 'HIS Integration', icon: <HisIcon /> },
    { key: 'ris', label: 'RIS Sync', icon: <SyncIcon /> },
    { key: 'detection', label: 'Detection', icon: <DetectionIcon /> },
    { key: 'system', label: 'System', icon: <SystemIcon /> },
  ]

  // Compact styles
  const inputSx = { '& .MuiInputBase-root': { fontSize: '0.8rem' }, '& .MuiInputLabel-root': { fontSize: '0.75rem' } };
  const tableSx = { '& .MuiTableCell-root': { py: 0.5, px: 1, fontSize: '0.75rem' } };

  return (
    <Box sx={{ p: 1 }}>
      {/* Header - Compact */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Typography sx={{ fontSize: '0.9rem', fontWeight: 600 }}>Task Management</Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <FormControl size="small" sx={{ minWidth: 70, ...inputSx }}>
            <InputLabel>Ore</InputLabel>
            <Select value={historyHours} label="Ore" onChange={(e) => setHistoryHours(Number(e.target.value))}>
              <MenuItem value={1} sx={{ fontSize: '0.75rem' }}>1h</MenuItem>
              <MenuItem value={6} sx={{ fontSize: '0.75rem' }}>6h</MenuItem>
              <MenuItem value={24} sx={{ fontSize: '0.75rem' }}>24h</MenuItem>
              <MenuItem value={48} sx={{ fontSize: '0.75rem' }}>48h</MenuItem>
            </Select>
          </FormControl>
          <Chip label={autoRefresh ? 'Auto' : 'Man'} color={autoRefresh ? 'success' : 'default'} size="small" onClick={() => setAutoRefresh(!autoRefresh)} sx={{ height: 20, fontSize: '0.65rem' }} />
          <IconButton onClick={() => fetchDashboard(true)} disabled={refreshing} size="small" sx={{ p: 0.5 }}>{refreshing ? <CircularProgress size={14} /> : <RefreshIcon sx={{ fontSize: 16 }} />}</IconButton>
        </Box>
      </Box>

      {/* Summary Stats - Compact inline */}
      <Box sx={{ display: 'flex', gap: 1, mb: 1, flexWrap: 'wrap' }}>
        {data.category_stats.map((cat) => (
          <Paper key={cat.category} onClick={() => setActiveCategory(cat.category)} sx={{ px: 1.5, py: 0.75, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 1, border: activeCategory === cat.category ? '2px solid' : '1px solid', borderColor: activeCategory === cat.category ? getCategoryColor(cat.category) : 'divider', minWidth: 120, '&:hover': { borderColor: getCategoryColor(cat.category) } }}>
            <Box sx={{ color: getCategoryColor(cat.category), '& svg': { fontSize: 16 } }}>{getCategoryIcon(cat.category)}</Box>
            <Box>
              <Typography sx={{ fontSize: '0.68rem', color: 'text.secondary' }}>{cat.display_name}</Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Typography sx={{ fontSize: '0.95rem', fontWeight: 700 }}>{cat.total}</Typography>
                <Chip label={`✓${cat.success}`} size="small" color="success" variant="outlined" sx={{ height: 14, fontSize: '0.58rem', '& .MuiChip-label': { px: 0.5 } }} />
                {cat.failed > 0 && <Chip label={`✗${cat.failed}`} size="small" color="error" variant="outlined" sx={{ height: 14, fontSize: '0.58rem', '& .MuiChip-label': { px: 0.5 } }} />}
              </Box>
            </Box>
          </Paper>
        ))}
      </Box>

      <Grid container spacing={1}>
        {/* Left Column - Scheduled Tasks & Queues */}
        <Grid item xs={12} md={4}>
          {/* Scheduled Tasks */}
          <Paper sx={{ p: 1, mb: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.75 }}>
              <ScheduleIcon sx={{ fontSize: 16, color: getCategoryColor(activeCategory) }} />
              <Typography sx={{ fontSize: '0.78rem', fontWeight: 600 }}>Task-uri Programate</Typography>
            </Box>
            {data.scheduled_tasks[activeCategory]?.length > 0 ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                {data.scheduled_tasks[activeCategory].map((task) => (
                  <Box key={task.task_key} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 0.5, borderBottom: '1px solid', borderColor: 'divider' }}>
                    <Box sx={{ flex: 1 }}>
                      <Typography sx={{ fontSize: '0.75rem', fontWeight: 500 }}>{task.name}</Typography>
                      <Typography sx={{ fontSize: '0.65rem', color: 'text.secondary' }}>{task.schedule}</Typography>
                      <Typography sx={{ fontSize: '0.62rem', color: 'text.disabled' }}>{task.description}</Typography>
                    </Box>
                    <IconButton size="small" color="primary" onClick={() => handleTriggerTask(task.task_key)} disabled={triggeringTask === task.task_key} sx={{ p: 0.25 }}>
                      {triggeringTask === task.task_key ? <CircularProgress size={14} /> : <PlayIcon sx={{ fontSize: 16 }} />}
                    </IconButton>
                  </Box>
                ))}
              </Box>
            ) : (
              <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary' }}>Niciun task programat</Typography>
            )}
          </Paper>

          {/* Queues */}
          <Paper sx={{ p: 1, mb: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.75 }}>
              <QueueIcon sx={{ fontSize: 16 }} color="primary" />
              <Typography sx={{ fontSize: '0.78rem', fontWeight: 600 }}>Cozi ({data.summary.queue_total} în așteptare)</Typography>
            </Box>
            {data.queues.map((queue) => (
              <Box key={queue.name} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 0.25 }}>
                <Typography sx={{ fontSize: '0.72rem' }}>{queue.display_name}</Typography>
                <Chip label={queue.length} size="small" color={queue.status === 'healthy' ? 'success' : queue.status === 'warning' ? 'warning' : 'error'} variant={queue.length > 0 ? 'filled' : 'outlined'} sx={{ height: 18, fontSize: '0.65rem' }} />
              </Box>
            ))}
          </Paper>

          {/* EasyMedical Stats (only for HIS category) */}
          {activeCategory === 'his' && data.summary.easymedical?.total_requests > 0 && (
            <Paper sx={{ p: 1 }}>
              <Typography sx={{ fontSize: '0.78rem', fontWeight: 600, mb: 0.5 }}>EasyMedical ({data.summary.easymedical.total_requests})</Typography>
              <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 0.5 }}>
                <Chip label={`Pend: ${data.summary.easymedical.pending}`} size="small" color="warning" variant="outlined" sx={{ height: 16, fontSize: '0.6rem' }} />
                <Chip label={`Proc: ${data.summary.easymedical.processing}`} size="small" color="info" variant="outlined" sx={{ height: 16, fontSize: '0.6rem' }} />
                <Chip label={`OK: ${data.summary.easymedical.completed}`} size="small" color="success" variant="outlined" sx={{ height: 16, fontSize: '0.6rem' }} />
              </Box>
              {data.summary.easymedical.recent_requests?.slice(0, 3).map((req) => (
                <Box key={req.request_id} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 0.25 }}>
                  <Typography sx={{ fontSize: '0.65rem' }}>{req.patient}</Typography>
                  <Chip label={req.status} size="small" color={getStatusColor(req.status)} sx={{ height: 14, fontSize: '0.55rem' }} />
                </Box>
              ))}
            </Paper>
          )}
        </Grid>

        {/* Right Column - Task History */}
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.75 }}>
              <Box sx={{ color: getCategoryColor(activeCategory), '& svg': { fontSize: 16 } }}>{getCategoryIcon(activeCategory)}</Box>
              <Typography sx={{ fontSize: '0.78rem', fontWeight: 600 }}>Istoric {categories.find(c => c.key === activeCategory)?.label}</Typography>
              <Chip label={`${data.recent_tasks[activeCategory]?.length || 0} task-uri`} size="small" sx={{ height: 18, fontSize: '0.65rem', ml: 0.5 }} />
            </Box>

            {data.recent_tasks[activeCategory]?.length > 0 ? (
              <TableContainer sx={{ maxHeight: 350, ...tableSx }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow sx={{ bgcolor: 'grey.900', '& th': { bgcolor: 'grey.900', color: 'common.white', fontSize: '0.7rem', fontWeight: 600, py: 0.5, px: 1 } }}>
                      <TableCell>Ora</TableCell>
                      <TableCell>Descriere</TableCell>
                      <TableCell>Pacient/Studiu</TableCell>
                      <TableCell align="center">Status</TableCell>
                      <TableCell align="right">Durată</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {data.recent_tasks[activeCategory].map((task) => (
                      <TableRow key={task.id} hover sx={{ '& td': { py: 0.5, px: 1 } }}>
                        <TableCell><Typography sx={{ fontSize: '0.7rem' }}>{formatTime(task.timestamp)}</Typography></TableCell>
                        <TableCell>
                          <Typography sx={{ fontSize: '0.72rem' }}>{task.description}</Typography>
                          {task.pacs_name && <Typography sx={{ fontSize: '0.62rem', color: 'text.secondary' }}>PACS: {task.pacs_name}</Typography>}
                        </TableCell>
                        <TableCell>
                          {task.patient_info && <Typography sx={{ fontSize: '0.72rem', fontWeight: 500 }}>{task.patient_info}</Typography>}
                          {task.study_info && <Typography sx={{ fontSize: '0.62rem', color: 'text.secondary' }}>{task.study_info}</Typography>}
                          {!task.patient_info && !task.study_info && <Typography sx={{ fontSize: '0.65rem', color: 'text.disabled' }}>-</Typography>}
                        </TableCell>
                        <TableCell align="center">
                          <Tooltip title={task.error_message || ''} arrow>
                            <Chip icon={task.status === 'success' ? <SuccessIcon sx={{ fontSize: '12px !important' }} /> : task.status === 'failed' ? <ErrorIcon sx={{ fontSize: '12px !important' }} /> : undefined} label={task.status} size="small" color={getStatusColor(task.status)} sx={{ height: 18, fontSize: '0.62rem', '& .MuiChip-icon': { ml: 0.25, mr: -0.5 } }} />
                          </Tooltip>
                        </TableCell>
                        <TableCell align="right"><Typography sx={{ fontSize: '0.68rem' }}>{formatDuration(task.duration_seconds)}</Typography></TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Box sx={{ textAlign: 'center', py: 2 }}>
                <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>Niciun task în această categorie în ultimele {historyHours} ore</Typography>
              </Box>
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* Summary Footer - Compact */}
      <Box sx={{ mt: 1, display: 'flex', justifyContent: 'center', gap: 2 }}>
        <Typography sx={{ fontSize: '0.68rem', color: 'text.secondary' }}>Total: {data.summary.total_tasks} task-uri</Typography>
        <Typography sx={{ fontSize: '0.68rem', color: 'success.main' }}>Succes: {data.summary.success_rate}%</Typography>
        {data.summary.failed_count > 0 && <Typography sx={{ fontSize: '0.68rem', color: 'error.main' }}>Eșuate: {data.summary.failed_count}</Typography>}
      </Box>
    </Box>
  )
}

export default TaskManagementTab
